import {
  IGetUmrahList,
  IUmrahFilterOptionData,
  IUmrahFilterState,
  IUmrahSearchQueryParams,
} from "../types/umrah-search-type";
import {
  getUmrahDurationBucket,
  getUmrahGroupSizeBucket,
} from "./extract-umrah-filter-data";

function parsePrice(value: string | number | null | undefined): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const normalized = Number(value.replaceAll(",", ""));
    if (Number.isFinite(normalized)) {
      return normalized;
    }
  }

  return null;
}

function parseNumber(value: string | number | null | undefined): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
}

function unique(values?: string[]) {
  return Array.from(new Set((values ?? []).map((item) => item.trim()))).filter(
    Boolean,
  );
}

export function buildUmrahFilterState(
  searchParams: IUmrahSearchQueryParams,
  optionData: IUmrahFilterOptionData,
): IUmrahFilterState {
  const minPrice = searchParams.min_price ?? optionData.minPrice;
  const maxPrice = searchParams.max_price ?? optionData.maxPrice;

  return {
    query: searchParams.query ?? "",
    minPrice: Number.isFinite(minPrice) ? minPrice : optionData.minPrice,
    maxPrice: Number.isFinite(maxPrice) ? maxPrice : optionData.maxPrice,
    durations: unique(searchParams.duration),
    groupSizes: unique(searchParams.group_size),
  };
}

export function clampUmrahPriceRange(
  state: IUmrahFilterState,
  optionData: IUmrahFilterOptionData,
): IUmrahFilterState {
  const nextMin = Math.max(
    state.minPrice ?? optionData.minPrice,
    optionData.minPrice,
  );
  const nextMax = Math.min(
    state.maxPrice ?? optionData.maxPrice,
    optionData.maxPrice,
  );

  return {
    ...state,
    minPrice: Math.min(nextMin, nextMax),
    maxPrice: Math.max(nextMin, nextMax),
  };
}

export function filterUmrahResult(
  result: IGetUmrahList[] | undefined,
  filters: IUmrahFilterState,
): IGetUmrahList[] {
  if (!result?.length) {
    return [];
  }

  const query = filters.query.trim().toLowerCase();

  return result.filter((umrah) => {
    const price = parsePrice(umrah.adult_price);
    const durationBucket = getUmrahDurationBucket(Number(umrah.duration));
    const groupSizeValue = parseNumber(umrah.group_size);
    const groupSizeBucket =
      typeof groupSizeValue === "number"
        ? getUmrahGroupSizeBucket(groupSizeValue)
        : null;

    const title = umrah.title?.toLowerCase() ?? "";
    const description = umrah.short_description?.toLowerCase() ?? "";

    if (query && !title.includes(query) && !description.includes(query)) {
      return false;
    }

    if (
      typeof filters.minPrice === "number" &&
      price !== null &&
      price < filters.minPrice
    ) {
      return false;
    }

    if (
      typeof filters.maxPrice === "number" &&
      price !== null &&
      price > filters.maxPrice
    ) {
      return false;
    }

    if (
      filters.durations.length > 0 &&
      !filters.durations.includes(durationBucket)
    ) {
      return false;
    }

    if (
      filters.groupSizes.length > 0 &&
      (!groupSizeBucket || !filters.groupSizes.includes(groupSizeBucket))
    ) {
      return false;
    }

    return true;
  });
}
